<div [ngBusy]="{busy: busy, message: ('common.pleaseWait' | translate)}">
  @if (deliveryAddresses?.length) {
    <div class="mb-4">
      <span class="text-pink pointer delivery-address"
            (click)="chooseDeliveryAddress()"
            [innerHTML]="'orderProcess.delivery.deliveryForms.chooseDeliveryAddress' | translate"
      >Choose a saved delivery address</span>
    </div>
  }

  <form [formGroup]="deliveryForm" class="delivery-to-door-form">
    <div class="row">
      <div class="col-6 col-lg-5 col-xl-4">
        <div class="form-group form-group-xl">
          <label class="d-block" [translate]="'common.postNumber'"></label>

          <ui-input-loader-wrap [loading]="postNumBusy">
            <input class="form-control"
                   type="tel"
                   [placeholder]="('common.postNumberPlaceholder_' + countryCode) | translate"
                   [inputMaskArgs]="{countryCode: countryCode}"
                   [inputMask]="'postNumber'"
                   [formControlName]="'deliveryPostNumber'">
          </ui-input-loader-wrap>
        </div>
      </div>

      <div class="col-6 col-lg-7 col-xl-8">
        <div class="form-group form-group-xl">
          <label class="d-block" [translate]="'common.postalArea'"></label>

          @if (canContinueDelivery()) {
            <input class="form-control"
                   readonly
                   [placeholder]="'common.postalArea' | translate"
                   [formControlName]="'deliveryCity'">
          } @else {
            <div class="disabled-input-div">
              {{ reserveCityName || ('common.notFound' | translate) }}
            </div>

            <div class="d-none d-md-block text-danger not-available-msg mt-lg-2"
                 [translate]="'orderProcess.delivery.deliveryForms.notAvailable'">
            </div>
          }
        </div>
      </div>

      @if (!canContinueDelivery()) {
        <div class="col-12 mb-3 d-md-none">
          <div class="text-danger not-available-msg text-center"
               [translate]="'orderProcess.delivery.deliveryForms.notAvailable'">
          </div>

          @if (isAnotherDeliveryAvailable) {
            <div class="mt-3 font-md text-center text-color-default text-underline">
              <span [translate]="'orderProcess.delivery.deliveryForms.chooseAnotherDeliveryInstead'"
                    (click)="onChooseAnotherDelivery.emit()">Pick up in store instead?</span>
            </div>
          }
        </div>
      }
    </div>

    @if (deliveryPrices()) {
      <div class="delivery-days-wrap mb-4 mt-1">
        <div class="delivery-days-title">{{ 'orderProcess.cakesOverview.delivery.times' | translate }}</div>

        <delivery-days-info
          [deliveryPrices]="deliveryPrices()"
          [currencySymbol]="currency"
          [overwritingDelivery]="overwritingDelivery"
        ></delivery-days-info>

        @if (deliveryScheduleOverrides?.length) {
          <custom-delivery-times class="d-block mt-3"
                                 [deliveryScheduleOverrides]="deliveryScheduleOverrides"
                                 [currencySymbol]="currency">
          </custom-delivery-times>
        }
      </div>
    }

    @if (isCityValid) {
      @if (deliveryNoteForCustomer || availableProductTypes?.length) {
        <deliveries-notes
          class="d-block my-3"
          [deliveryNoteForCustomer]="deliveryNoteForCustomer"
          [availableProductTypes]="availableProductTypes">
        </deliveries-notes>
      }

      <daily-stock-sold-out-notice
        class="d-block my-3"
        [productTypes]="dailyStockSoldOutProductTypes"
      ></daily-stock-sold-out-notice>

      @if (fullCapacityDays?.length) {
        <sold-out-msg
          class="d-block my-3"
          [fullCapacityDays]="fullCapacityDays"
        ></sold-out-msg>
      }

      <ui-datetime-skeleton [loading]="skeletonPromise">
        <div class="row">
          <div class="col-6 col-lg-4">
            <div class="form-group form-group-xl">
              <label #datePickerLabel
                     [translate]="'common.delivery.deliveryDate'"></label>

              <ui-delivery-datepicker
                [calendar]="calendar()"
                [disabled]="isCalendarLoading"
                [formControlName]="'deliveryDate'"
                [placement]="'bottom-left'"
                (onMonthNavigate)="onMonthNavigate($event)"
                (onContextInit)="contextInit($event)"
              ></ui-delivery-datepicker>
            </div>
          </div>

          <div class="col-6 col-lg-4">
            <div class="form-group form-group-xl">
              <label [translate]="'orderProcess.delivery.deliveryForms.chooseTime'"></label>

              <div class="dropdown-container dropdown-item-slim delivery-to-door-time">
                <ui-dropdown
                  #rangeDropDown
                  [items]="deliveryTimeItems"
                  [placeholder]="'common.timePlaceholder' | translate"
                  [formControlName]="'deliveryTime'"
                  [placement]="'bottom'"
                  (changeItem)="timeChanged($event)"
                ></ui-dropdown>
              </div>
            </div>
          </div>

          @if (showDeliveryPricesInfo && isPriceSet) {
            <div class="col-12 col-lg-4">
              <div class="form-group form-group-xl clearfix">
                <label class="price-label"
                       [translate]="'orderProcess.delivery.deliveryForms.deliveryPrice'">
                </label>

                <div class="price-nok">
                  {{ price | currencyFormat: currency }}
                </div>
              </div>
            </div>
          }
        </div>
      </ui-datetime-skeleton>

      @if (isDateTimeValid()) {
        <div class="mb-5">
          <div class="form-group form-group-xl">
            <label [translate]="'common.delivery.deliveryAddress'"></label>

            <input class="form-control"
                   [placeholder]="'orderProcess.delivery.deliveryForms.deliveryAddressPlaceholder' | translate"
                   [formControlName]="'deliveryAddress'">
          </div>

          <div class="form-group form-group-xl">
            <label [translate]="'orderProcess.delivery.deliveryForms.recipientName'"></label>
            <input class="form-control"
                   [maxlength]="'128'"
                   [placeholder]="'common.name' | translate"
                   [formControlName]="'deliveryUserName'">
          </div>

          <div class="form-group form-group-xl">
            <label [translate]="'orderProcess.delivery.deliveryForms.recipientPhone'"></label>

            @if (countryCode) {
              <ui-i18n-phone-input
                *libphonenumber
                [maxlength]="'32'"
                [defaultCountryCode]="countryCode"
                [showLabel]="false"
                [placeholder]="'common.telephone' | translate"
                [formControlName]="'deliveryPhone'"
              ></ui-i18n-phone-input>
            }
          </div>

          <div class="form-group form-group-xl">
            <label [translate]="'orderProcess.delivery.deliveryForms.messageToDriver'"></label>

            <textarea class="form-control" rows="3"
                      [maxlength]="'512'"
                      [placeholder]="'orderProcess.delivery.deliveryForms.messageToDriverPlaceholder'| translate"
                      [formControlName]="'deliveryNote'"
            ></textarea>
          </div>

          @if (possibleToRegister && deliveryAddresses === null && deliveryForm.valid) {
            <div class="delivery-address d-flex align-items-center mb-4 alert-circle-wrap">
              <i class="icon-alert-circle text-orange me-2 font-3xl"></i>
              <div>
                <span class="text-muted"
                      [translate]="'orderProcess.delivery.deliveryForms.signUpBenefit'">
                  Save delivery details for future purchases?</span>&nbsp;
                <a href="#" (click)="$event.preventDefault(); onRegister.emit()"
                   [translate]="'orderProcess.delivery.deliveryForms.signUp'"
                >Create an account</a>
              </div>
            </div>
          }

          @if (deliveryAddresses !== null && deliveryForm.valid && isCurrentAddressNotSaved) {
            <div class="delivery-address d-flex align-items-center mb-4 alert-circle-wrap">
              <ui-checkbox [(model)]="saveAddressOnSubmit">
                {{ 'orderProcess.delivery.deliveryForms.saveDeliveryAddress' | translate }}
              </ui-checkbox>
            </div>
          }

          <div class="confirm-btn-wrap">
            <button class="btn {{nextBtnView}} btn-mobile-full-width font-xl delivery-confirm-btn"
                    [disabled]="isNextBtnDisabled()"
                    (click)="confirmDelivery()">
              <span [translate]="nextBtnTextKey"></span>
            </button>
          </div>
        </div>
      }
    }
  </form>
</div>
